import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TextSizeSwitcher } from '@/components/TextSizeSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { FontSwitcher } from '@/components/FontSwitcher';
import { TimeFormatSwitcher } from '@/components/TimeFormatSwitcher';
import { MealThumbnailSizeSwitcher } from '@/components/MealThumbnailSizeSwitcher';
import { ServingSizeToggle } from '@/components/ServingSizeToggle';
import { TagPlacementSwitcher } from '@/components/TagPlacementSwitcher';
import { SummaryStatsToggle } from '@/components/SummaryStatsToggle';
import { KeyFindingsToggle } from '@/components/KeyFindingsToggle';
import { NutrientTimingPrefs } from '@/components/NutrientTimingPrefs';
import { SummaryLayoutPrefs } from '@/components/SummaryLayoutPrefs';

/** Hash the sidebar PreferencesMenu uses to deep-link here. */
export const DISPLAY_PREFERENCES_HASH = 'display-preferences';

/**
 * Bundles the per-device display/format preferences (text size, theme, font,
 * meal-photo size, summary stats, nutrient timing, summary layout) into one
 * compact card. Each control renders in its `section` variant so they read as
 * a tidy list instead of stacked cards. Lives at the bottom of the Profile
 * page — the sidebar PreferencesMenu keeps quick text-size / language toggles
 * and links here for the full set.
 */
export const DisplayPreferencesCard = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);

  // Sidebar "Display preferences" deep-links with #display-preferences.
  // Scroll once the card is mounted so the clinician lands on this section
  // rather than the top of Profile.
  useEffect(() => {
    if (location.hash !== `#${DISPLAY_PREFERENCES_HASH}`) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <Card ref={cardRef} id={DISPLAY_PREFERENCES_HASH}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          {t('Display preferences')}
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y">
        <div className="pb-5">
          <TextSizeSwitcher variant="section" />
        </div>
        <div className="py-5">
          <ThemeSwitcher variant="section" />
        </div>
        <div className="py-5">
          <FontSwitcher variant="section" />
        </div>
        <div className="py-5">
          <TimeFormatSwitcher variant="section" />
        </div>
        <div className="py-5">
          <MealThumbnailSizeSwitcher variant="section" />
        </div>
        <div className="py-5">
          <ServingSizeToggle variant="section" />
        </div>
        <div className="py-5">
          <TagPlacementSwitcher variant="section" />
        </div>
        <div className="py-5">
          <SummaryStatsToggle variant="section" />
          <KeyFindingsToggle variant="section" />
        </div>
        <div className="py-5">
          <NutrientTimingPrefs variant="section" />
        </div>
        <div className="pt-5">
          <SummaryLayoutPrefs variant="section" />
        </div>
      </CardContent>
    </Card>
  );
};
